
import React from "react";

export default function Hero() {
  return (
    <section className="relative min-h-screen flex flex-col overflow-hidden bg-brand-navy pt-20 lg:pt-24">
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/222d376a6438c06cc721f17853fba047.jpg"
          alt="Florida Energy Efficient Neighborhood"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/75 via-black/60 to-black/80"></div>
      </div>

      {/* Main hero content */}
      <div className="relative z-10 max-w-[1400px] mx-auto px-4 sm:px-6 py-10 lg:py-16 w-full flex-1 flex items-center">
        <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-12 w-full">
          {/* Left content */}
          <div className="flex-1 max-w-2xl">
            {/* Promo badge */}
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-1.5 mb-6 lg:mb-8 animate-[pulse_3s_ease-in-out_infinite]">
              <i className="ri-fire-fill text-brand-red text-base w-4 h-4 flex items-center justify-center"></i>
              <span className="text-white font-bold text-xs sm:text-sm whitespace-nowrap">
                LIMITED TIME OFFER
              </span>
            </div>

            <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-white mb-4 lg:mb-6 leading-tight">
              Save Up To <span className="text-brand-red">60%</span> On Your Florida Energy Bills
            </h1>

            <p className="text-base lg:text-lg text-white/80 mb-6 lg:mb-8 leading-relaxed">
              Complete HVAC, Solar &amp; Insulation solutions for Florida homeowners. Get a free home
              assessment and start saving today.
            </p>

            {/* Offer cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-8 lg:mb-10">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 sm:p-5 border border-white/20">
                <div className="flex items-center gap-2 mb-1.5">
                  <i className="ri-sun-line text-brand-gold text-lg w-5 h-5 flex items-center justify-center"></i>
                  <span className="font-bold text-xs text-white">SOLAR SPECIAL</span>
                </div>
                <p className="text-xl sm:text-2xl font-bold mb-0.5 text-white">30% Tax Credit</p>
                <p className="text-white/60 text-xs sm:text-sm">Federal Solar Investment Tax Credit</p>
              </div>
              <div className="bg-brand-red/80 backdrop-blur-sm rounded-xl p-4 sm:p-5 border border-brand-red/40">
                <div className="flex items-center gap-2 mb-1.5">
                  <i className="ri-snowflake-line text-white text-lg w-5 h-5 flex items-center justify-center"></i>
                  <span className="font-bold text-xs text-white">HVAC DEAL</span>
                </div>
                <p className="text-xl sm:text-2xl font-bold mb-0.5 text-white">$0 Down Financing</p>
                <p className="text-white/70 text-xs sm:text-sm">On qualifying heat pump systems</p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <a
                href="#calculator"
                className="bg-brand-red text-white px-6 lg:px-8 py-3.5 lg:py-4 rounded-full text-sm lg:text-base font-semibold hover:bg-brand-red-dark transition-all duration-300 hover:scale-105 text-center whitespace-nowrap cursor-pointer shadow-lg shadow-brand-red/25"
              >
                Calculate Your Savings
              </a>
              <a
                href="#services"
                className="bg-white/10 backdrop-blur-sm border-2 border-white/40 text-white px-6 lg:px-8 py-3.5 lg:py-4 rounded-full text-sm lg:text-base font-semibold hover:bg-white hover:text-brand-navy transition-all duration-300 text-center whitespace-nowrap cursor-pointer"
              >
                Explore Services
              </a>
            </div>
          </div>

          {/* Right visual — hero image */}
          <div className="flex-1 relative hidden lg:flex items-center justify-center">
            <div className="relative w-full max-w-[560px]">
              <div className="rounded-3xl overflow-hidden shadow-2xl border-2 border-white/20">
                <img
                  src="https://readdy.ai/api/search-image?query=Beautiful%20modern%20Florida%20home%20exterior%20with%20solar%20panels%20on%20white%20roof%2C%20bright%20sunny%20day%2C%20clean%20professional%20real%20estate%20photography%2C%20lush%20tropical%20landscaping%20with%20palm%20trees%20%2C%20energy%20efficient%20home%2C%20pristine%20white%20stucco%20walls%2C%20blue%20sky%20with%20soft%20clouds%2C%20high%20end%20residential%20property%2C%20warm%20inviting%20atmosphere%20%2C%20front%20view%20of%20house&width=560&height=480&seq=hero-white-bg-101&orientation=landscape"
                  alt="Energy Efficient Florida Home"
                  className="w-[560px] h-[480px] object-cover object-top"
                />
              </div>

              {/* Floating savings card */}
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl border border-gray-100 p-5 animate-[float_3s_ease-in-out_infinite]">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <i className="ri-money-dollar-circle-line text-green-600 text-2xl w-6 h-6 flex items-center justify-center"></i>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Avg. Annual Savings</p>
                    <p className="text-2xl font-bold text-brand-navy">$2,400+</p>
                  </div>
                </div>
              </div>

              {/* Floating rating card */}
              <div className="absolute -top-4 -right-4 bg-white rounded-2xl shadow-xl border border-gray-100 p-4 animate-[float_3s_ease-in-out_infinite_0.5s]">
                <div className="flex items-center gap-2">
                  <div className="flex text-brand-gold text-base">
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                    <i className="ri-star-fill"></i>
                  </div>
                  <span className="text-sm font-bold text-gray-900">5.0</span>
                </div>
                <p className="text-xs text-gray-500 mt-1">500+ Happy Customers</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom trust bar */}
      <div className="relative z-10 w-full max-w-[1200px] mx-auto px-4 sm:px-6 pb-6 lg:pb-8">
        <div className="bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 p-4 sm:p-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 items-center">
            <div className="flex items-center gap-2">
              <svg viewBox="0 0 272 92" className="h-5 sm:h-7 w-auto flex-shrink-0">
                <path fill="#EA4335" d="M115.75 47.18c0 12.77-9.99 22.18-22.25 22.18s-22.25-9.41-22.25-22.18C71.25 34.32 81.24 25 93.5 25s22.25 9.32 22.25 22.18zm-9.74 0c0-7.98-5.79-13.44-12.51-13.44S80.99 39.2 80.99 47.18c0 7.9 5.79 13.44 12.51 13.44s12.51-5.55 12.51-13.44z"/>
                <path fill="#FBBC05" d="M163.75 47.18c0 12.77-9.99 22.18-22.25 22.18s-22.25-9.41-22.25-22.18c0-12.85 9.99-22.18 22.25-22.18s22.25 9.32 22.25 22.18zm-9.74 0c0-7.98-5.79-13.44-12.51-13.44s-12.51 5.46-12.51 13.44c0 7.9 5.79 13.44 12.51 13.44s12.51-5.55 12.51-13.44z"/>
                <path fill="#4285F4" d="M209.75 26.34v39.82c0 16.38-9.66 23.07-21.08 23.07-10.75 0-17.22-7.19-19.66-13.07l8.48-3.53c1.51 3.61 5.21 7.87 11.17 7.87 7.31 0 11.84-4.51 11.84-13v-3.19h-.34c-2.18 2.69-6.38 5.04-11.68 5.04-11.09 0-21.25-9.66-21.25-22.09 0-12.52 10.16-22.26 21.25-22.26 5.29 0 9.49 2.35 11.68 4.96h.34v-3.61h9.25zm-8.56 20.92c0-7.81-5.21-13.52-11.84-13.52-6.72 0-12.35 5.71-12.35 13.52 0 7.73 5.63 13.36 12.35 13.36 6.63 0 11.84-5.63 11.84-13.36z"/>
                <path fill="#34A853" d="M225 3v65h-9.5V3h9.5z"/>
                <path fill="#EA4335" d="M262.02 54.48l7.56 5.04c-2.44 3.61-8.32 9.83-18.48 9.83-12.6 0-22.01-9.74-22.01-22.18 0-13.19 9.49-22.18 20.92-22.18 11.51 0 17.14 9.16 18.98 14.11l1.01 2.52-29.65 12.28c2.27 4.45 5.8 6.72 10.75 6.72 4.96 0 8.4-2.44 10.92-6.14zm-23.27-7.98l19.82-8.23c-1.09-2.77-4.37-4.7-8.23-4.7-4.95 0-11.84 4.37-11.59 12.93z"/>
                <path fill="#4285F4" d="M35.29 41.41V32H67c.31 1.64.47 3.58.47 5.68 0 7.06-1.93 15.79-8.15 22.01-6.05 6.3-13.78 9.66-24.02 9.66C16.32 69.35.36 53.89.36 34.91.36 15.93 16.32.47 35.3.47c10.5 0 17.98 4.12 23.6 9.49l-6.64 6.64c-4.03-3.78-9.49-6.72-16.97-6.72-13.86 0-24.7 11.17-24.7 25.03 0 13.86 10.84 25.03 24.7 25.03 8.99 0 14.11-3.61 17.39-6.89 2.66-2.66 4.41-6.46 5.1-11.65l-22.49.01z"/>
              </svg>
              <div className="flex text-brand-gold text-sm sm:text-lg">
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
              </div>
              <span className="text-xs sm:text-sm font-semibold text-white">4.9</span>
            </div>

            <div className="flex items-center gap-2">
              <svg viewBox="0 0 24 24" className="w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0" fill="none">
                <path d="M12 1.5l3.09 6.26L22 8.64l-5 4.87 1.18 6.88L12 17.27l-6.18 3.25L7 13.64 2 8.77l6.91-1.01L12 1.5z" fill="#00B67A"/>
              </svg>
              <span className="text-xs sm:text-sm font-bold text-[#00B67A]">Trustpilot</span>
              <div className="flex text-[#00B67A] text-sm sm:text-lg">
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
                <i className="ri-star-fill"></i>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <svg viewBox="0 0 100 100" className="w-9 h-9 sm:w-12 sm:h-12 flex-shrink-0">
                <rect width="100" height="100" rx="8" fill="#012E5E"/>
                <text x="50" y="42" fontFamily="Arial, sans-serif" fontWeight="900" fontSize="32" fill="white" textAnchor="middle">BBB</text>
                <circle cx="50" cy="70" r="18" fill="white"/>
                <text x="50" y="78" fontFamily="Arial, sans-serif" fontWeight="bold" fontSize="20" fill="#012E5E" textAnchor="middle">A+</text>
              </svg>
              <span className="text-xs sm:text-sm font-semibold text-white">Accredited</span>
            </div>

            <div className="text-left">
              <p className="text-xs sm:text-sm font-semibold text-white">500+ Happy Florida Homeowners</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
